// components/customers/AgingReport.jsx
// Analyse des retards de paiement — créances par tranche de jours
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { apiFetch } from '../../api.js'
import { Download, RefreshCw, Loader2, AlertTriangle, TrendingUp } from 'lucide-react'

function cx(...classes) {
  return classes.filter(Boolean).join(' ')
}

function fmtMoney(n) {
  const x = Number(n || 0)
  return Number.isFinite(x) ? x.toLocaleString('fr-FR') + ' XOF' : '0 XOF'
}

function BucketBar({ value, total, tone }) {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0
  if (pct === 0) return <div className="text-xs text-slate-300">—</div>
  const colors = {
    green:  'bg-emerald-500',
    yellow: 'bg-amber-400',
    orange: 'bg-orange-500',
    red:    'bg-rose-600',
  }
  return (
    <div className="flex items-center gap-2">
      <div className="h-1.5 w-16 rounded-full bg-slate-100 overflow-hidden">
        <div className={cx('h-full rounded-full', colors[tone])} style={{ width: `${pct}%` }} />
      </div>
      <span className={cx('text-xs font-semibold tabular-nums', {
        green: 'text-emerald-700',
        yellow: 'text-amber-700',
        orange: 'text-orange-700',
        red: 'text-rose-700',
      }[tone])}>{pct}%</span>
    </div>
  )
}

export default function AgingReport() {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState('')
  const [search, setSearch] = useState('')
  const [downloading, setDownloading] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    setErr('')
    try {
      const res = await apiFetch('/api/customers/aging')
      setData(res)
    } catch (e) {
      setErr(e?.message || 'Erreur chargement')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  async function handleExportCsv() {
    setDownloading(true)
    try {
      const res = await fetch('/api/customers/aging?format=csv', {
        credentials: 'include',
        headers: { 'X-CSRF-Token': document.cookie.match(/csrf_token=([^;]+)/)?.[1] || '' },
      })
      if (!res.ok) throw new Error('Erreur export CSV')
      const blob = await res.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `retards-paiement-${new Date().toISOString().slice(0, 10)}.csv`
      a.click()
      URL.revokeObjectURL(url)
    } catch (e) {
      alert('Erreur CSV : ' + e.message)
    } finally {
      setDownloading(false)
    }
  }

  const rows = useMemo(() => {
    const list = Array.isArray(data?.rows) ? data.rows : []
    if (!search.trim()) return list
    const q = search.trim().toLowerCase()
    return list.filter(r =>
      String(r.name || '').toLowerCase().includes(q) ||
      String(r.phone || '').includes(q)
    )
  }, [data, search])

  const totals = data?.totals || {}
  const grandTotal = Number(totals.total_due || 0)

  // Pastilles de résumé des 4 buckets
  const buckets = [
    { key: 'bucket_0_30',   label: '0–30 j',  tone: 'green'  },
    { key: 'bucket_31_60',  label: '31–60 j', tone: 'yellow' },
    { key: 'bucket_61_90',  label: '61–90 j', tone: 'orange' },
    { key: 'bucket_90plus', label: '90j+',    tone: 'red'    },
  ]

  const bucketColors = {
    green:  'bg-emerald-50 border-emerald-200 text-emerald-900',
    yellow: 'bg-amber-50  border-amber-200  text-amber-900',
    orange: 'bg-orange-50 border-orange-200 text-orange-900',
    red:    'bg-rose-50   border-rose-200   text-rose-900',
  }
  const bucketLabel = {
    green:  'text-emerald-700',
    yellow: 'text-amber-700',
    orange: 'text-orange-700',
    red:    'text-rose-700',
  }

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-slate-400">
        <Loader2 className="h-8 w-8 animate-spin mb-2" />
        <span className="text-sm">Chargement…</span>
      </div>
    )
  }

  if (err) {
    return (
      <div className="mx-auto max-w-2xl mt-10 rounded-2xl bg-rose-50 border border-rose-200 p-6 text-center">
        <AlertTriangle className="h-8 w-8 text-rose-500 mx-auto mb-2" />
        <div className="text-sm text-rose-700 font-semibold">{err}</div>
        <button onClick={load} className="mt-4 px-4 py-2 rounded-xl bg-rose-600 text-white text-sm font-bold hover:bg-rose-700">
          Réessayer
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Titre + actions */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-xl font-black text-slate-900">Analyse des retards de paiement</h2>
          <p className="mt-1 text-sm text-slate-500">
            {data?.count || 0} client(s) avec créances — Total dû : <strong>{fmtMoney(grandTotal)}</strong>
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={load}
            className="flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-600 hover:bg-slate-50"
          >
            <RefreshCw className="h-3.5 w-3.5" />
            Actualiser
          </button>
          <button
            onClick={handleExportCsv}
            disabled={downloading}
            className="flex items-center gap-1.5 rounded-xl bg-slate-900 px-3 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-60"
          >
            {downloading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
            CSV
          </button>
        </div>
      </div>

      {/* Récap 4 buckets */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {buckets.map(b => (
          <div key={b.key} className={cx('rounded-2xl border p-4', bucketColors[b.tone])}>
            <div className={cx('text-xs font-bold uppercase tracking-wide mb-1', bucketLabel[b.tone])}>
              {b.label}
            </div>
            <div className="text-lg font-black text-slate-900">
              {fmtMoney(totals[b.key] || 0)}
            </div>
            {grandTotal > 0 && (
              <div className={cx('text-xs mt-1 font-semibold', bucketLabel[b.tone])}>
                {Math.round((Number(totals[b.key] || 0) / grandTotal) * 100)}% du total
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Barre de recherche */}
      <div className="flex items-center gap-3">
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Filtrer par client ou téléphone…"
          className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 w-64"
        />
        {search && (
          <span className="text-xs text-slate-500">{rows.length} résultat(s)</span>
        )}
      </div>

      {/* Tableau */}
      {rows.length === 0 ? (
        <div className="rounded-2xl border border-slate-200 bg-white p-12 text-center text-slate-400">
          <TrendingUp className="mx-auto mb-2 h-8 w-8 opacity-40" />
          <div className="font-semibold">Aucune créance en cours</div>
          <div className="text-sm mt-1">Tous les clients sont à jour.</div>
        </div>
      ) : (
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50">
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide">Client</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-emerald-700 uppercase tracking-wide">0–30j</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-amber-700 uppercase tracking-wide">31–60j</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-orange-700 uppercase tracking-wide">61–90j</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-rose-700 uppercase tracking-wide">90j+</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase tracking-wide">Total dû</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide">Répartition</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map(r => {
                  const total = Number(r.total_due || 0)
                  const isHighRisk = Number(r.bucket_90plus) > total * 0.5
                  return (
                    <tr key={r.uid} className={cx('hover:bg-slate-50 transition-colors', isHighRisk && 'bg-rose-50/40')}>
                      <td className="px-4 py-3">
                        <div className="font-semibold text-slate-900">{r.name}</div>
                        {r.phone && <div className="text-xs text-slate-500">{r.phone}</div>}
                        <div className="text-[11px] text-slate-400 mt-0.5">{r.nb_orders} cmde(s)</div>
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums text-emerald-700 font-medium">
                        {Number(r.bucket_0_30) > 0 ? fmtMoney(r.bucket_0_30) : <span className="text-slate-200">—</span>}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums text-amber-700 font-medium">
                        {Number(r.bucket_31_60) > 0 ? fmtMoney(r.bucket_31_60) : <span className="text-slate-200">—</span>}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums text-orange-700 font-medium">
                        {Number(r.bucket_61_90) > 0 ? fmtMoney(r.bucket_61_90) : <span className="text-slate-200">—</span>}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums text-rose-700 font-bold">
                        {Number(r.bucket_90plus) > 0 ? fmtMoney(r.bucket_90plus) : <span className="text-slate-200">—</span>}
                      </td>
                      <td className="px-4 py-3 text-right font-black text-slate-900 tabular-nums">
                        {fmtMoney(total)}
                      </td>
                      <td className="px-4 py-3">
                        <div className="space-y-0.5">
                          {buckets.map(b => Number(r[b.key]) > 0 && (
                            <BucketBar key={b.key} value={Number(r[b.key])} total={total} tone={b.tone} />
                          ))}
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
              {/* Ligne de total */}
              <tfoot>
                <tr className="border-t-2 border-slate-200 bg-slate-50 font-bold">
                  <td className="px-4 py-3 text-sm text-slate-700">TOTAL ({data?.count || 0} clients)</td>
                  <td className="px-4 py-3 text-right text-emerald-700 tabular-nums">{fmtMoney(totals.bucket_0_30)}</td>
                  <td className="px-4 py-3 text-right text-amber-700 tabular-nums">{fmtMoney(totals.bucket_31_60)}</td>
                  <td className="px-4 py-3 text-right text-orange-700 tabular-nums">{fmtMoney(totals.bucket_61_90)}</td>
                  <td className="px-4 py-3 text-right text-rose-700 tabular-nums">{fmtMoney(totals.bucket_90plus)}</td>
                  <td className="px-4 py-3 text-right text-slate-900 tabular-nums">{fmtMoney(grandTotal)}</td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}
